import Layout from 'components/ui/Layout';
import { PAGE_CONTENT_WIDTH } from 'utils/uiHelpers';
import PageHeader from 'components/ui/PageHeader';
import { AddCategoryModal } from 'features/main-category/components/AddMainCategoryModal';
import { DeleteConfirmModal } from 'features/main-category/components/DeleteConfirmModal';
import { EditCategoryModal } from 'features/main-category/components/EditCategoryModal';
import { ViewCategoryModal } from 'features/main-category/components/ViewMainCategoryModal';
import { useMainCategories } from 'features/main-category/hooks/useMainCategories';
import { usePrefetchMainCategories } from 'features/main-category/hooks/usePrefetchMainCategories';
import { useToggleCategoryStatus } from 'features/main-category/hooks/useToggleCategoryStatus';
import { useURLSync } from 'features/main-category/hooks/useURLSync';
import { useUserProfile } from 'features/auth/hooks/useUserProfile';
import { mainCategoryServices } from 'features/main-category/services/main-category.service';
import {
  ChevronLeft,
  ChevronRight,
  Download,
  Edit,
  Eye,
  Plus,
  Search,
  Trash2,
  X,
  FolderTree,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { formatDateTime } from 'utils/dateFormatter';

const MainCategory = () => {
  const [filters, setFilters] = useURLSync({
    page: '1',
    limit: '10',
    level: 'all',
    isActive: 'all',
    isPopular: 'all',
    isFeatured: 'all',
    search: '',
    sortBy: 'displayOrder',
    sortOrder: 'asc',
  });

  const [searchInput, setSearchInput] = useState('');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);

  const { data, isPending, isError, error } = useMainCategories(filters);
  const { mutate: toggleStatus } = useToggleCategoryStatus();
  const { prefetchNextPage, prefetchPrevPage } = usePrefetchMainCategories();

  const { data: profileData } = useUserProfile();
    const user = profileData?.data;
 
// console.log('Current Role:', user?.role?.name);
 
const restrictedRoles = [
  'delivery_manager',
  'travel_manager',
  'operations_manager',
  'finance_manager'
];
 
const showPermissionError = restrictedRoles.includes(
  user?.role?.name
);
 

  useEffect(() => {
    if (data?.pagination) {
      const { currentPage, totalPages } = data.pagination;
      prefetchNextPage(filters, totalPages);
      if (currentPage > 1) {
        prefetchPrevPage(filters);
      }
    }
  }, [data, filters]);

  const updateFilter = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      page: key === 'page' ? value : '1',
    }));
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== filters.search) {
        updateFilter('search', searchInput);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setSearchInput(filters.search);
  }, [filters.search]);

  const handleSort = (column) => {
    const newOrder =
      filters.sortBy === column && filters.sortOrder === 'asc' ? 'desc' : 'asc';
    setFilters((prev) => ({
      ...prev,
      sortBy: column,
      sortOrder: newOrder,
    }));
  };

  // Export handler
const handleExport = async () => {
  try {
    const response =
      await mainCategoryServices.exportCategories({
        level: filters.level,
        isActive: filters.isActive,
        isPopular: filters.isPopular,
        isFeatured: filters.isFeatured,
        search: filters.search,
        sortBy: filters.sortBy,
        sortOrder: filters.sortOrder,
      });

    const blob = new Blob([response.data], {
      type: 'text/csv',
    });

    const url = window.URL.createObjectURL(blob);

    const link = document.createElement('a');

    link.href = url;
    link.download = `categories-${Date.now()}.csv`;

    document.body.appendChild(link);

    link.click();

    document.body.removeChild(link);

    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error(
      'Failed to export categories:',
      error
    );
  }
};

  // Toggle status handler with optimistic update
  const handleToggleStatus = (categoryId, currentStatus) => {
    toggleStatus({ categoryId, isActive: !currentStatus });
  };

  const SortIcon = ({ column }) => {
    if (filters.sortBy !== column)
      return <span className='text-muted-foreground'>↕</span>;
    return filters.sortOrder === 'asc' ? <span>↑</span> : <span>↓</span>;
  };

  const handleEditClick = (category) => {
    setIsEditModalOpen(true);
    setSelectedCategory(category);
  };

  const handleDeleteClick = (category) => {
    setSelectedCategory(category);
    setIsDeleteModalOpen(true);
  };

  const handleViewClick = (category) => {
    setSelectedCategory(category);
    setIsViewModalOpen(true);
  };

  const getLevelBadgeColor = (level) => {
    const colors = [
      'bg-blue-500/10 text-blue-600 dark:text-blue-400',
      'bg-purple-500/10 text-purple-600 dark:text-purple-400',
      'bg-pink-500/10 text-pink-600 dark:text-pink-400',
    ];
    return colors[level] || 'bg-gray-500/10 text-gray-600 dark:text-gray-400';
  };

  return (
    <Layout>
      <div className={`min-h-screen bg-background text-foreground p-6 ${PAGE_CONTENT_WIDTH}`}>
          {/* Header */}
          <PageHeader
            icon={FolderTree}
            title="Main Category Management"
            subtitle="Manage service categories, hierarchy and display settings"
            actions={
              !showPermissionError && (
                <button
                  onClick={handleExport}
                  className='flex items-center space-x-2 px-4 py-2 text-sm border border-border rounded-md bg-card text-card-foreground hover:bg-accent transition-colors'
                >
                  <Download size={16} />
                  <span>Export</span>
                </button>
              )
            }
          />

          {/* Filters */}
          <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3 mb-6'>
            {/* Search */}
            <div className='relative lg:col-span-2'>
              <Search
                className='absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground'
                size={16}
              />
              <input
                type='text'
                placeholder='Search by name or slug...'
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className='w-full pl-10 pr-10 py-2 text-sm'
              />
              {searchInput && (
                <button
                  onClick={() => setSearchInput('')}
                  className='absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground'
                >
                  <X size={16} />
                </button>
              )}
            </div>

            {/* Level Filter */}
            {/* <select
              value={filters.level}
              onChange={(e) => updateFilter('level', e.target.value)}
              className='px-3 py-2 text-sm'
            >
              <option value='all'>All Levels</option>
              <option value='0'>Level 0 (Main)</option>
              <option value='1'>Level 1 (Sub)</option>
              <option value='2'>Level 2 (Child)</option>
            </select> */}

            {/* Active Status Filter */}
            {/* <select
              value={filters.isActive}
              onChange={(e) => updateFilter('isActive', e.target.value)}
              className='px-3 py-2 text-sm'
            >
              <option value='all'>All Status</option>
              <option value='true'>Active</option>
              <option value='false'>Inactive</option>
            </select> */}

            {/* Popular Filter */}
            {/* <select
              value={filters.isPopular}
              onChange={(e) => updateFilter('isPopular', e.target.value)}
              className='px-3 py-2 text-sm'
            >
              <option value='all'>All Popular</option>
              <option value='true'>Popular</option>
              <option value='false'>Not Popular</option>
            </select> */}

            {/* Items per page */}
            <select
              value={filters.limit}
              onChange={(e) => updateFilter('limit', e.target.value)}
              className='px-3 py-2 text-sm'
            >
              <option value='5'>5 per page</option>
              <option value='10'>10 per page</option>
              <option value='20'>20 per page</option>
              <option value='50'>50 per page</option>
            </select>
          </div>

          {/* Table */}
          <div className='bg-card border border-border rounded-lg overflow-hidden elevation-1'>
            <div className='overflow-x-auto custom-scrollbar'>
              <table className='w-full'>
                <thead className='bg-muted border-b border-border'>
                  <tr>
                    <th className='px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase'>
                      Icon
                    </th>
                    <th
                      className='px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase cursor-pointer hover:text-foreground transition-hover'
                      onClick={() => handleSort('name.english')}
                    >
                      <div className='flex items-center space-x-1'>
                        <span>Name</span>
                        <SortIcon column='name.english' />
                      </div>
                    </th>
                    <th
                      className='px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase cursor-pointer hover:text-foreground transition-hover'
                      onClick={() => handleSort('slug')}
                    >
                      <div className='flex items-center space-x-1'>
                        <span>Slug</span>
                        <SortIcon column='slug' />
                      </div>
                    </th>
                    {/* <th
                      className='px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase cursor-pointer hover:text-foreground transition-hover'
                      onClick={() => handleSort('level')}
                    >
                      <div className='flex items-center space-x-1'>
                        <span>Level</span>
                        <SortIcon column='level' />
                      </div>
                    </th> */}
                    {/* <th
                      className='px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase cursor-pointer hover:text-foreground transition-hover'
                      onClick={() => handleSort('displayOrder')}
                    >
                      <div className='flex items-center space-x-1'>
                        <span>Order</span>
                        <SortIcon column='displayOrder' />
                      </div>
                    </th> */}
                    {/* <th className='px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase'>
                      Status
                    </th>
                    <th className='px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase'>
                      Popular
                    </th>
                    <th className='px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase'>
                      Featured
                    </th> */}
                    {/* <th
                      className='px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase cursor-pointer hover:text-foreground transition-hover'
                      onClick={() => handleSort('createdAt')}
                    >
                      <div className='flex items-center space-x-1'>
                        <span>Created</span>
                        <SortIcon column='createdAt' />
                      </div>
                    </th> */}
                    <th className='px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase'>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className='divide-y divide-border'>
                  {
                  
                  showPermissionError ? (
    <tr>
      <td
        colSpan='11'
        className='px-4 py-8 text-center text-destructive'
      >
        Error loading CMS: You do not have permission to perform this action!
      </td>
    </tr>
  ): isPending ? (
                    <tr>
                      <td
                        colSpan={10}
                        className='px-4 py-8 text-center text-muted-foreground'
                      >
                        Loading categories...
                      </td>
                    </tr>
                  ) : isError ? (
                    <tr>
                      <td
                        colSpan={10}
                        className='px-4 py-8 text-center text-destructive'
                      >
                        Error loading categories:{' '}
                        {error?.message || 'Something went wrong'}
                      </td>
                    </tr>
                  ) : data?.data?.length === 0 ? (
                    <tr>
                      <td
                        colSpan={10}
                        className='px-4 py-8 text-center text-muted-foreground'
                      >
                        No categories found
                      </td>
                    </tr>
                  ) : (
                    data?.data?.map((category) => (
                      <tr
                        key={category._id}
                        className='hover:bg-accent/50 transition-colors'
                      >
                        {/* Icon Column */}
                        <td className='px-4 py-3'>
                          {category.image ? (
                            <img
                              src={category.image}
                              alt={category.name?.english}
                              className='w-10 h-10 rounded-lg object-cover border border-border'
                            />
                          ) : (
                            <div className='w-10 h-10 rounded-lg bg-muted flex items-center justify-center'>
                              <span className='text-xs text-muted-foreground'>
                                No Icon
                              </span>
                            </div>
                          )}
                        </td>

                        {/* Name Column */}
                        <td className='px-4 py-3 text-sm font-medium'>
                          <div>
                            <div className='text-foreground'>
                              {category.name?.english}
                            </div>
                          </div>
                        </td>

                        {/* Slug Column */}
                        <td className='px-4 py-3 text-sm text-muted-foreground'>
                          {category.slug}
                        </td>

                        {/* Level Column */}
                        {/* <td className='px-4 py-3 text-sm'>
                          <span
                            className={`whitespace-nowrap px-2 py-1 text-xs font-medium rounded-full ${getLevelBadgeColor(
                              category.level,
                            )}`}
                          >
                            Level {category.level}
                          </span>
                        </td> */}

                        {/* Display Order Column */}
                        {/* <td className='px-4 py-3 text-sm text-center'>
                          <span className='px-2 py-1 bg-secondary text-secondary-foreground rounded-md text-xs font-medium'>
                            {category.displayOrder}
                          </span>
                        </td> */}

                        {/* Status Column - NEW SWITCH */}
                        {/* <td className='px-4 py-3 text-sm'>
                          <label className='relative inline-flex items-center cursor-pointer group'>
                            <input
                              type='checkbox'
                              checked={category.isActive}
                              onChange={() =>
                                handleToggleStatus(
                                  category._id,
                                  category.isActive,
                                )
                              }
                              className='sr-only peer'
                            />
                            <div className="w-11 h-6 bg-gray-200 dark:bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-green-600"></div>
                            <span className='ms-3 text-sm font-medium text-foreground group-hover:text-primary transition-colors'>
                              {category.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </label>
                        </td> */}

                        {/* Popular Column */}
                        {/* <td className='px-4 py-3 text-sm'>
                          <span
                            className={`px-2 py-1 text-xs font-medium rounded-full ${
                              category.isPopular
                                ? 'bg-orange-500/10 text-orange-600 dark:text-orange-400'
                                : 'bg-gray-500/10 text-gray-600 dark:text-gray-400'
                            }`}
                          >
                            {category.isPopular ? 'Popular' : 'Regular'}
                          </span>
                        </td> */}

                        {/* Featured Column */}
                        {/* <td className='px-4 py-3 text-sm'>
                          <span
                            className={`px-2 py-1 text-xs font-medium rounded-full ${
                              category.isFeatured
                                ? 'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400'
                                : 'bg-gray-500/10 text-gray-600 dark:text-gray-400'
                            }`}
                          >
                            {category.isFeatured ? 'Featured' : 'Normal'}
                          </span>
                        </td> */}

                        {/* Created Date Column */}
                        {/* <td className='px-4 py-3 text-sm text-muted-foreground'>
                          {formatDateTime(category.createdAt)}
                        </td> */}

                        {/* Actions Column */}
                         <td className='px-4 py-3 text-sm text-right'>
                          <div className='flex items-center justify-end space-x-2'>
                            <button
                              className='p-1 hover:bg-muted rounded transition-colors'
                              title='View Details'
                              onClick={() => handleViewClick(category)}
                            >
                              <Eye
                                size={16}
                                className='text-muted-foreground hover:text-foreground'
                              />
                            </button>
                            <button
                              className='p-1 hover:bg-muted rounded transition-colors'
                              title='Edit Category'
                              onClick={() => handleEditClick(category)}
                            >
                              <Edit
                                size={16}
                                className='text-muted-foreground hover:text-foreground'
                              />
                            </button>
                            {/* <button
                              onClick={() => handleDeleteClick(category)}
                              className='p-1 hover:bg-muted rounded transition-colors'
                              title='Delete Category'
                            >
                              <Trash2
                                size={16}
                                className='text-muted-foreground hover:text-destructive'
                              />
                            </button> */}
                          </div>
                        </td> 
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {!showPermissionError && data?.pagination && (
              <div className='flex flex-col sm:flex-row items-center justify-between px-4 py-3 border-t border-border space-y-3 sm:space-y-0'>
                <div className='text-sm text-muted-foreground'>
                  Showing{' '}
                  {(data.pagination.currentPage - 1) *
                    data.pagination.itemsPerPage +
                    1}{' '}
                  to{' '}
                  {Math.min(
                    data.pagination.currentPage * data.pagination.itemsPerPage,
                    data.pagination.totalItems,
                  )}{' '}
                  of {data.pagination.totalItems} categories
                </div>

                <div className='flex items-center space-x-2'>
                  <button
                    onClick={() =>
                      updateFilter(
                        'page',
                        String(data.pagination.currentPage - 1),
                      )
                    }
                    disabled={data.pagination.currentPage === 1}
                    className='p-2 border border-border rounded-md bg-card hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
                  >
                    <ChevronLeft size={16} />
                  </button>

                  <div className='flex items-center space-x-1'>
                    {[...Array(data.pagination.totalPages)].map((_, idx) => {
                      const page = idx + 1;
                      const current = data.pagination.currentPage;

                      if (
                        page === 1 ||
                        page === data.pagination.totalPages ||
                        (page >= current - 1 && page <= current + 1)
                      ) {
                        return (
                          <button
                            key={page}
                            onClick={() => updateFilter('page', String(page))}
                            className={`min-w-[2rem] px-3 py-1 text-sm rounded-md transition-colors ${
                              page === current
                                ? 'bg-primary text-primary-foreground font-medium'
                                : 'bg-card hover:bg-accent border border-border'
                            }`}
                          >
                            {page}
                          </button>
                        );
                      } else if (page === current - 2 || page === current + 2) {
                        return (
                          <span key={page} className='text-muted-foreground'>
                            ...
                          </span>
                        );
                      }
                      return null;
                    })}
                  </div>

                  <button
                    onClick={() =>
                      updateFilter(
                        'page',
                        String(data.pagination.currentPage + 1),
                      )
                    }
                    disabled={
                      data.pagination.currentPage === data.pagination.totalPages
                    }
                    className='p-2 border border-border rounded-md bg-card hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            )}
          </div>

        {/* Modals */}
        <AddCategoryModal
          isAddModalOpen={isAddModalOpen}
          setIsAddModalOpen={setIsAddModalOpen}
        />
        <EditCategoryModal
          isEditModalOpen={isEditModalOpen}
          setIsEditModalOpen={setIsEditModalOpen}
          category={selectedCategory}
        />

        <DeleteConfirmModal
          isDeleteModalOpen={isDeleteModalOpen}
          setIsDeleteModalOpen={setIsDeleteModalOpen}
          category={selectedCategory}
        />
        <ViewCategoryModal
          isViewModalOpen={isViewModalOpen}
          setIsViewModalOpen={setIsViewModalOpen}
          category={selectedCategory}
        />
      </div>
    </Layout>
  );
};

export default MainCategory;
